import { useToast, ContextMenuItem, MenuItem } from '@cad-arc';
import { FieldNameEnum } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { createApiErrorHandler } from '@m43/core';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useCanTransitionToUnitStateGroup } from '../../common/hooks/use-can-transition-to-unit-state-group';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useCallback } from 'react';
import { useSetUnitsOffDutyApi } from '../../unit-context-menu/use-set-units-off-duty-api';
import { useNavigate } from 'react-router-dom';
import { FR_EDIT_UNIT_PATH } from '../../router/constants';
import { useDrawerControls } from '../../drawers/components/drawer-context-provider';
import { useUnitEmergency } from '../../unit-emergency/use-unit-emergency';

export const OffDutyItem = observer(function OffDutyItem({
    unitId,
    unitStateId,
    isAssignedToEvent,
}: {
    unitId: number;
    unitStateId: number;
    isAssignedToEvent: boolean;
}): React.ReactElement {
    const canTransitionToUnitStateGroup = useCanTransitionToUnitStateGroup(unitStateId);
    const setUnitsOffDuty = useSetUnitsOffDutyApi();
    const bootstrapState = useBootstrapState();
    const toast = useToast();
    const { isFirstResponder } = useApplicationType();
    const navigate = useNavigate();
    const drawerState = useDrawerControls();
    const { activeUnitEmergency } = useUnitEmergency();

    const setOffDuty = useCallback(
        (unitId: number) => {
            setUnitsOffDuty([unitId])
                .then(() => {
                    if (isFirstResponder) {
                        navigate(FR_EDIT_UNIT_PATH);
                        drawerState.close();
                    }
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('transitioning unit to Off Duty'),
                        });
                    })
                );
        },
        [setUnitsOffDuty, isFirstResponder, navigate, drawerState, toast]
    );

    const isDisabled =
        !!activeUnitEmergency || isAssignedToEvent || !canTransitionToUnitStateGroup('OFF_DUTY');
    const displayName =
        bootstrapState.fieldDisplayNameByFieldName[FieldNameEnum.DISPLAY_CAD_UNIT_OFF_DUTY.name] ??
        'Off Duty';

    const MenuComponent = isFirstResponder ? MenuItem : ContextMenuItem;

    return (
        <MenuComponent isDisabled={isDisabled} onSelect={() => setOffDuty(unitId)}>
            {displayName}
        </MenuComponent>
    );
});
